/**
 * AI 계정 연결 상태 기계 — **헤드리스.**
 *
 * 어시스턴트는 **사용자 본인 계정**으로 동작한다(서버 공용 키를 쓰지 않는다).
 * 연결 방법이 둘이고 **로그인이 기본**이다:
 *   1) 로그인 — 서버가 CLI 로그인을 대신 몰고, 사용자는 자기 브라우저에서 승인만 한다.
 *      PC 에 CLI 를 깔 필요도 터미널을 열 필요도 없고, 만료되면 CLI 가 자동 갱신한다.
 *   2) 토큰 — `claude setup-token` 출력을 붙여넣는다. 브라우저를 못 여는 환경(폐쇄망·자동화)용.
 *      서버가 암호화해 보관한다.
 * 둘 다 있으면 로그인이 쓰인다 — 서버 `threadRunner` 의 선택 규칙과 같다.
 *
 * 원본: openflow `AssistantAccount.tsx`(290줄) 의 상태 부분.
 * **화면은 올리지 않았다** — Tailwind(openflow)와 styled-components(QBase)가 갈린다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistantClient, AssistantAccount, LoginStart } from './client';

/** 제공자별 표시 이름 — 화면이 그대로 써도 되고 자기 것을 써도 된다. */
export const PROVIDER_LABEL: Record<string, string> = {
  claude: 'Claude (Claude Code)',
  codex: 'Codex (ChatGPT)',
};

/** 제공자마다 모델 이름 체계가 다르다. `default` 는 그 CLI 의 기본값을 그대로 쓴다는 뜻이다. */
export const PROVIDER_MODELS: Record<string, string[]> = {
  claude: ['default', 'sonnet', 'opus', 'haiku'],
  codex: ['default', 'gpt-5-codex', 'gpt-5'],
};

export interface UseAssistantAccountOptions {
  client: AssistantClient;
  provider: string;
  /** 계정 상태가 바뀌면 알린다 — 대화 훅의 `refreshAccount` 를 물려 두면 화면이 같이 갱신된다 */
  onChanged?: (a: AssistantAccount) => void;
  /** 로그아웃·토큰 삭제 전 확인. 없으면 바로 진행한다(앱의 공통 확인 모달을 넣을 자리) */
  confirm?: (message: string) => Promise<boolean> | boolean;
}

export function useAssistantAccount(opts: UseAssistantAccountOptions) {
  const { client, provider, onChanged, confirm } = opts;

  const [account, setAccount] = useState<AssistantAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [login, setLogin] = useState<Partial<LoginStart> | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const apply = useCallback((a: AssistantAccount) => {
    setAccount(a);
    if (onChangedRef.current) onChangedRef.current(a);
  }, []);

  const refresh = useCallback(async () => {
    try { apply(await client.getAccount(provider)); }
    catch { /* 다음 시도에 다시 본다 */ }
  }, [client, provider, apply]);

  useEffect(() => { refresh(); }, [refresh]);

  // 처음 읽은 계정에 진행 중 로그인이 있으면 이어받는다(새로고침 후에도 대기 화면이 유지되도록).
  useEffect(() => {
    if (account && account.pendingLogin && !login) setLogin(account.pendingLogin);
    // account 가 바뀔 때만 — login 을 의존에 넣으면 취소 직후 되살아난다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  /**
   * 코드를 되돌려주지 않는 흐름(Codex 디바이스 인증 · 브라우저 자동 복귀)은 CLI 가 스스로
   * 승인을 감지한다. 요청을 몇 분씩 붙들지 않도록 화면이 계정 상태만 짧게 확인한다.
   */
  useEffect(() => {
    if (!login || login.needsCode) return undefined;
    let stop = false;
    const timer = setInterval(async () => {
      if (stop) return;
      try {
        const a = await client.getAccount(provider);
        if (stop) return;
        apply(a);
        if (a.loggedIn) {
          stop = true; setLogin(null);
          setNotice(a.email ? `${a.email} 로 연결되었습니다.` : '연결되었습니다.');
        } else if (a.pendingLogin && (a.pendingLogin as any).state === 'failed') {
          stop = true; setLogin(null);
          setError((a.pendingLogin as any).message || '로그인에 실패했습니다.');
        } else if (!a.pendingLogin) {
          stop = true; setLogin(null);
        }
      } catch { /* 폴링 실패는 조용히 넘긴다 */ }
    }, 3000);
    return () => { stop = true; clearInterval(timer); };
  }, [login, client, provider, apply]);

  /**
   * 로그인 시작.
   *
   * ★ 빈 탭을 **클릭 그 순간** 열어 두고 나중에 주소를 넣는다. `await` 뒤에 `window.open` 을
   *   부르면 사용자 제스처가 끝난 뒤라 브라우저가 조용히 막고, 화면에는 아무 일도 안 일어난
   *   것처럼 보인다. (openflow 에서 실측한 함정)
   */
  const beginLogin = useCallback(async () => {
    let tab: Window | null = null;
    try { tab = window.open('', '_blank'); } catch { tab = null; }
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await client.startLogin(provider);
      setLogin(r);
      if (tab && !tab.closed) tab.location.href = r.url;
      else setNotice('팝업이 막혔습니다. 아래 주소를 직접 열어 승인하세요.');
    } catch (e: any) {
      if (tab && !tab.closed) tab.close();
      setError(e?.message || '로그인을 시작하지 못했습니다.');
    } finally { setBusy(false); }
  }, [client, provider]);

  const finishLogin = useCallback(async (code: string) => {
    if (!login?.loginId || !code.trim()) return;
    setBusy(true); setError('');
    try {
      const r = await client.submitLoginCode(login.loginId, code.trim());
      setLogin(null);
      setNotice(r.email ? `${r.email} 로 연결되었습니다.` : '연결되었습니다.');
      await refresh();
    } catch (e: any) {
      setError(e?.message || '코드 확인에 실패했습니다.');
    } finally { setBusy(false); }
  }, [client, login, refresh]);

  const abortLogin = useCallback(async () => {
    setLogin(null);
    try { await client.cancelLogin(); } catch { /* 서버가 정리 못 해도 화면은 되돌린다 */ }
  }, [client]);

  const logout = useCallback(async () => {
    if (confirm && !(await confirm('AI 계정 연결을 끊습니다. 계속할까요?'))) return;
    setBusy(true);
    try { await client.logout(provider); setNotice('연결을 끊었습니다.'); await refresh(); }
    catch (e: any) { setError(e?.message || '로그아웃에 실패했습니다.'); }
    finally { setBusy(false); }
  }, [client, provider, refresh, confirm]);

  const saveToken = useCallback(async (token: string) => {
    if (!token.trim()) return;
    setBusy(true); setError('');
    try {
      const r = await client.saveToken(token.trim(), provider);
      setNotice(`토큰을 등록했습니다 (${r.hint}).`);
      await refresh();
    } catch (e: any) {
      setError(e?.message || '토큰 등록에 실패했습니다.');
    } finally { setBusy(false); }
  }, [client, provider, refresh]);

  const removeToken = useCallback(async () => {
    if (confirm && !(await confirm('등록한 토큰을 지웁니다. 계속할까요?'))) return;
    setBusy(true);
    try { await client.removeToken(provider); setNotice('토큰을 지웠습니다.'); await refresh(); }
    catch (e: any) { setError(e?.message || '토큰 삭제에 실패했습니다.'); }
    finally { setBusy(false); }
  }, [client, provider, refresh, confirm]);

  const saveApiKey = useCallback(async (apiKey: string) => {
    if (!apiKey.trim()) return;
    setBusy(true); setError('');
    try {
      const r = await client.saveApiKey(apiKey.trim(), provider);
      setNotice(`API 키를 등록했습니다 (${r.hint}).`);
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'API 키 등록에 실패했습니다.');
    } finally { setBusy(false); }
  }, [client, provider, refresh]);

  const removeApiKey = useCallback(async () => {
    if (confirm && !(await confirm('등록한 API 키를 지웁니다. 계속할까요?'))) return;
    setBusy(true);
    try { await client.removeApiKey(provider); setNotice('API 키를 지웠습니다.'); await refresh(); }
    catch (e: any) { setError(e?.message || 'API 키 삭제에 실패했습니다.'); }
    finally { setBusy(false); }
  }, [client, provider, refresh, confirm]);

  return {
    account, busy, error, notice, login,
    loggedIn: !!(account && account.loggedIn),
    models: PROVIDER_MODELS[provider] || ['default'],
    providerLabel: PROVIDER_LABEL[provider] || provider,
    setError, setNotice,
    refresh, beginLogin, finishLogin, abortLogin, logout, saveToken, removeToken,
    saveApiKey, removeApiKey,
  };
}

export default useAssistantAccount;
